import { useState, useEffect, useCallback } from "react";

const STORAGE_KEY = "badge-sound-enabled";

/**
 * Hook to manage badge sound preference (stored in localStorage)
 * Default is enabled (true)
 */
export function useBadgeSoundPreference() {
  const [soundEnabled, setSoundEnabledState] = useState<boolean>(() => {
    if (typeof window === "undefined") return true;
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored === null ? true : stored === "true";
  });

  const setSoundEnabled = useCallback((enabled: boolean) => {
    setSoundEnabledState(enabled);
    localStorage.setItem(STORAGE_KEY, String(enabled));
  }, []);

  // Sync across tabs
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) {
        setSoundEnabledState(e.newValue === null ? true : e.newValue === "true");
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);

  return { soundEnabled, setSoundEnabled };
}

/**
 * Utility function to check if badge sound is enabled
 * Can be used outside of React components
 */
export function isBadgeSoundEnabled(): boolean {
  if (typeof window === "undefined") return true;
  const stored = localStorage.getItem(STORAGE_KEY);
  return stored === null ? true : stored === "true";
}
